import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { mustSave, useSaveState } from "../../lib/saving";
import { SaveBar, SaveError, SaveStatus } from "../../components/save";
import {MoneyField, PastDateField, TextField } from "../../components/fields";
import {
  Button, Field, Select, OptionSelect, ErrorNote, Confirm, Pill, Panel, type Status,
} from "../../components/ui";
import { COLORS } from "../../lib/options";
import VehiclePhotos from "./VehiclePhotos";
import VehicleDocs from "./VehicleDocs";
import VehicleListing from "./VehicleListing";
import { formatMoney, parseMoney, centsToInput } from "../../lib/money";
import {
  listExpenses, addExpense, updateExpense, deleteExpense, summarise,
  CATEGORIES, CATEGORY_LABEL, STATES, STATE_LABEL, VENDORS,
  type VehicleExpense, type ExpenseCategory, type ExpenseState,
} from "./expenses";
import { checkOdometer, checkMoney } from "../../lib/validation";
import { TITLE_STATUSES } from "../../lib/options";

interface Vehicle {
  id: string;
  stock_no: string;
  vin: string;
  year: number;
  make: string;
  model: string;
  trim_level: string | null;
  body_type: string | null;
  engine: string | null;
  transmission: string | null;
  drivetrain: string | null;
  fuel_type: string | null;
  exterior_color: string | null;
  odometer: number;
  purchase_date: string;
  purchased_from: string;
  purchase_price_cents: number;
  total_cost_cents: number;
  asking_price_cents: number;
  /* The listing panel keeps its text here rather than in the page. */
  listing_body: string | null;
  min_price_cents: number;
  title_status: string;
  title_number: string | null;
  status: Status;
  status_changed_at: string;
}

const TITLE_LABEL: Record<string, string> = {
  not_received: "Not received",
  in_hand: "In hand",
  sent_to_state: "Sent to state",
  with_lienholder: "With lienholder",
};

/** The edit form as it would be for this row, untouched. */
const formFrom = (row: Vehicle) => ({
  odometer: String(row.odometer),
  purchase: centsToInput(row.purchase_price_cents),
  ask: centsToInput(row.asking_price_cents),
  minPrice: centsToInput(row.min_price_cents),
  titleStatus: row.title_status,
  titleNumber: row.title_number ?? "",
  color: row.exterior_color ?? "",
  trim: row.trim_level ?? "",
});

const STATUS_NAME: Record<string, string> = {
  purchased: "Purchased", in_recon: "In recon", available: "Available", on_hold: "On deposit",
};

const daysOn = (iso: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 864e5));

export default function VehicleDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [v, setV] = useState<Vehicle | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [expenses, setExpenses] = useState<VehicleExpense[]>([]);
  const [expErr, setExpErr] = useState<string | null>(null);
  const [showExpense, setShowExpense] = useState(false);
  const [editingExpense, setEditingExpense] = useState<string | null>(null);
  const blankExpense = {
    incurred_on: new Date().toISOString().slice(0, 10),
    category: "parts" as ExpenseCategory,
    state: "completed" as ExpenseState,
    description: "", vendor: "", vendorOther: "", invoice: "", amount: "",
  };
  const [ex, setEx] = useState(blankExpense);
  /* The expense form as it was opened, so "unsaved" means changed. */
  const exStart = useRef(blankExpense);
  const [confirming, setConfirming] = useState(false);
  /* Which expense is waiting to be confirmed. Removing a line changes what
     the car cost, so it is not a thing to do on one stray tap. */
  const [confirmExpense, setConfirmExpense] = useState<VehicleExpense | null>(null);
  const [errs, setErrs] = useState<Record<string, string | null>>({});

  const [f, setF] = useState({
    odometer: "", purchase: "", ask: "", minPrice: "",
    titleStatus: "", titleNumber: "", color: "", trim: "",
  });

  useEffect(() => {
    supabase.from("vehicles").select("*").eq("id", id).single()
      .then(({ data, error }) => {
        if (error) { setLoadErr("Could not load this vehicle."); return; }
        const row = data as Vehicle;
        setV(row);
        setF(formFrom(row));
      });
    reloadExpenses();
  }, [id]);

  const set = (k: keyof typeof f) => (val: string) => setF((s) => ({ ...s, [k]: val }));

  /* Save state, one per thing that can be saved on this page, so a failure is
     reported beside the button that caused it. */
  const editDirty = editing && v !== null
    && JSON.stringify(f) !== JSON.stringify(formFrom(v));
  const editSave = useSaveState({ dirty: editDirty, what: "the vehicle changes" });
  const expDirty = showExpense && JSON.stringify(ex) !== JSON.stringify(exStart.current);
  const expSave = useSaveState({ dirty: expDirty, what: "this expense" });
  const expRemove = useSaveState();
  const actions = useSaveState();
  const busy = editSave.saving || actions.saving;
  /* Whichever expense change happened last, for the note left behind once
     the form has closed. */
  const lastExpense = expRemove.savedAt
      && (!expSave.savedAt || expRemove.savedAt > expSave.savedAt)
    ? { save: expRemove, label: "Expense removed" }
    : { save: expSave, label: "Expense saved" };
  const expBusy = expSave.saving || expRemove.saving;

  async function reloadExpenses() {
    try { setExpenses(await listExpenses(id)); setExpErr(null); }
    catch (e) { setExpErr(e instanceof Error ? e.message : "Could not read the expenses."); }
  }

  /* The car's cost, worked out the same way the database works it out. The
     database remains the authority — this is so the screen does not have to
     wait for a round trip to show the effect of a change. */
  const cost = summarise(v?.purchase_price_cents ?? 0, expenses);

  function startNewExpense() {
    exStart.current = blankExpense;
    setEx(blankExpense); setEditingExpense(null); setShowExpense(true); setExpErr(null);
    expSave.reset();
  }

  function startEditExpense(e: VehicleExpense) {
    /* A vendor already on the list opens with it selected; anything else
       opens as "Other" with the name in the box, so nothing is reassigned. */
    const known = (VENDORS as readonly string[]).includes(e.vendor ?? "");
    const start = {
      incurred_on: e.incurred_on, category: e.category, state: e.state,
      description: e.description,
      vendor: e.vendor ? (known ? e.vendor : "other") : "",
      vendorOther: e.vendor && !known ? e.vendor : "",
      invoice: e.invoice_no ?? "",
      amount: centsToInput(e.amount_cents),
    };
    exStart.current = start;
    setEx(start);
    setEditingExpense(e.id); setShowExpense(true); setExpErr(null);
    expSave.reset();
  }

  /* "Other" means the typed name; anything else is the name itself. */
  const vendorName = () =>
    ex.vendor === "other" ? ex.vendorOther.trim() : ex.vendor;

  async function saveExpense() {
    const cents = parseMoney(ex.amount);
    if (cents === null || cents < 0) { expSave.fail("Enter an amount."); return; }
    if (!ex.description.trim()) { expSave.fail("Say what the money went on."); return; }
    const draft = {
      incurred_on: ex.incurred_on,
      category: ex.category,
      state: ex.state,
      description: ex.description.trim(),
      vendor: vendorName() || null,
      invoice_no: ex.invoice.trim() || null,
      amount_cents: cents,
      notes: null,
    };
    const editingId = editingExpense;
    const ok = await expSave.run(
      () => (editingId ? updateExpense(editingId, draft) : addExpense(id, draft)),
      { toast: editingId ? "Expense saved" : "Expense added" });
    if (!ok) return;            // the form stays open with everything in it
    setShowExpense(false); setEditingExpense(null); setEx(blankExpense);
    // Read back after the save is confirmed; a failure to re-read is not a
    // failure to save, so it is reported separately.
    await reloadExpenses();
    await refreshTotal();
  }

  async function removeExpense(expenseId: string) {
    const ok = await expRemove.run(() => deleteExpense(expenseId), { toast: "Expense removed" });
    setConfirmExpense(null);
    if (!ok) return;
    await reloadExpenses();
    await refreshTotal();
  }

  /* The total is recomputed by the database, so it is read back rather than
     assumed here. */
  async function refreshTotal() {
    const { data } = await supabase
      .from("vehicles").select("total_cost_cents").eq("id", id).single();
    if (data) setV((cur) => (cur ? { ...cur, total_cost_cents: data.total_cost_cents } : cur));
  }

  async function changeStatus(status: Status) {
    if (!v) return;
    await actions.run(async () => {
      mustSave(await supabase.from("vehicles").update({ status }).eq("id", v.id).select("id"));
      setV({ ...v, status, status_changed_at: new Date().toISOString() });
    }, { toast: `Status changed to ${STATUS_NAME[status] ?? status}` });
  }

  async function saveEdits() {
    if (!v) return;
    const next = {
      odometer: checkOdometer(f.odometer),
      purchase: checkMoney(f.purchase, "Purchase price"),
      ask: checkMoney(f.ask, "Asking price"),
      minPrice: checkMoney(f.minPrice, "Minimum price"),
    };
    setErrs(next);
    if (!Object.values(next).every((e) => e === null)) {
      editSave.fail("Fix the fields marked in red, then press Save again.");
      return;
    }

    await editSave.run(async () => {
    // total_cost_cents is derived from the purchase price and the expense
    // lines by the database, so it is never sent.
    mustSave(await supabase.from("vehicles").update({
      odometer: Number(f.odometer.replace(/,/g, "")),
      purchase_price_cents: parseMoney(f.purchase) ?? 0,
      asking_price_cents: parseMoney(f.ask) ?? 0,
      min_price_cents: parseMoney(f.minPrice) ?? 0,
      title_status: f.titleStatus,
      title_number: f.titleNumber.trim() || null,
      exterior_color: f.color.trim() || null,
      trim_level: f.trim.trim() || null,
    }).eq("id", v.id).select("id"));

    const purchase = parseMoney(f.purchase) ?? 0;
    setV({
      ...v,
      odometer: Number(f.odometer.replace(/,/g, "")),
      purchase_price_cents: purchase,
      total_cost_cents: purchase + cost.expenses,
      asking_price_cents: parseMoney(f.ask) ?? 0,
      min_price_cents: parseMoney(f.minPrice) ?? 0,
      title_status: f.titleStatus,
      title_number: f.titleNumber.trim() || null,
      exterior_color: f.color.trim() || null,
      trim_level: f.trim.trim() || null,
    });
    setEditing(false);
    }, { toast: "Vehicle changes saved" });
  }

  /** Cancel asks first if anything was changed, then puts the form back. */
  function cancelEdits() {
    if (editDirty && !window.confirm("Discard your changes to this vehicle?")) return;
    if (v) setF(formFrom(v));
    setEditing(false); setErrs({}); editSave.dismiss();
  }

  /**
   * Deleting is for mistakes — a car entered twice, or the wrong VIN typed in.
   * A vehicle that has been sold is history and the foreign keys will refuse,
   * which is the right answer.
   */
  async function remove() {
    if (!v) return;
    const ok = await actions.run(async () => {
      mustSave(await supabase.from("vehicles").delete().eq("id", v.id).select("id"));
    }, { toast: `${v.year} ${v.make} ${v.model} deleted` });
    setConfirming(false);
    if (ok) onBack();
  }

  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>;
  if (!v) return <p className="hint">Loading</p>;

  const gross = v.asking_price_cents - v.total_cost_cents;
  /* Nothing has been asked for it yet. Gross against a zero price is not a
     loss, it is an unanswered question. */
  const priced = v.asking_price_cents > 0;

  return (
    <>

      <div className="head">
        <h2>{v.year} {v.make} {v.model}</h2>
        <Pill status={v.status} />
      </div>
      <p className="sub mono">{v.vin}</p>
      {!editing && editSave.status === "saved" && (
        <p className="savenote">
          <SaveStatus save={editSave} savedLabel="Vehicle changes saved" />
        </p>
      )}

      {!editing && (
        <>
          {/* The question you open a car to answer. A car with no asking
              price has not been priced yet, which is not the same as being
              worth nothing — showing a red loss there was simply wrong. */}
          <dl className="moneystrip">
            <div>
              <dt>Cost</dt>
              <dd>{formatMoney(v.total_cost_cents)}</dd>
            </div>
            <div>
              <dt>Asking</dt>
              {priced
                ? <dd>{formatMoney(v.asking_price_cents)}</dd>
                : <dd className="none">Not priced yet</dd>}
            </div>
            <div>
              <dt>Gross</dt>
              {priced
                ? <dd className={gross < 0 ? "down" : "up"}>{formatMoney(gross)}</dd>
                : <dd className="none">Set a price</dd>}
            </div>
          </dl>

          <Panel title="Money" rail="money"
                 figure={formatMoney(v.total_cost_cents)}>
            <dl className="spec">
              <div><dt>Purchase</dt><dd>{formatMoney(v.purchase_price_cents)}</dd></div>
              <div><dt>Expenses</dt><dd>{formatMoney(cost.expenses)}</dd></div>
              <div><dt>Asking</dt>
                <dd>{priced ? formatMoney(v.asking_price_cents) : "—"}</dd></div>
              <div><dt>Minimum</dt>
                <dd>{v.min_price_cents ? formatMoney(v.min_price_cents) : "—"}</dd></div>
            </dl>
          </Panel>

          <Panel title="The car" rail="record" note={v.stock_no}>
            <dl className="spec">
              <div><dt>Trim</dt><dd>{v.trim_level || "—"}</dd></div>
              <div><dt>Mileage</dt><dd>{v.odometer.toLocaleString("en-US")}</dd></div>
              <div><dt>Color</dt><dd>{v.exterior_color || "—"}</dd></div>
              <div><dt>Engine</dt><dd>{v.engine || "—"}</dd></div>
              <div><dt>Transmission</dt><dd>{v.transmission || "—"}</dd></div>
              <div><dt>Drivetrain</dt><dd>{v.drivetrain || "—"}</dd></div>
              <div><dt>Body</dt><dd>{v.body_type || "—"}</dd></div>
            </dl>
          </Panel>

          <VehiclePhotos vehicleId={id} stockNo={v.stock_no}
                         vehicleName={`${v.year} ${v.make} ${v.model}`} />

          <VehicleDocs vehicleId={id} />

          <VehicleListing vehicleId={id} vehicle={v}
                          onSaved={(saved) =>
                            setV((cur) => (cur ? { ...cur, ...saved } : cur))} />

          <Panel title="Expenses"
                 rail={cost.openLines > 0 ? "work" : "record"}
                 figure={formatMoney(cost.expenses)}
                 foot={!showExpense && (
                   <Button variant="ghost" onClick={startNewExpense}>
                     Add an expense
                   </Button>
                 )}>
          {expenses.length === 0 ? (
            <p className="hint">
              Nothing recorded yet. Transport, parts, paint, tires — each one
              goes here and adds to the car's cost.
            </p>
          ) : (
            <div className="item">
              {expenses.map((e) => (
                <div className={`li ${e.state === "cancelled" ? "neg" : ""}`} key={e.id}>
                  <span>
                    {e.incurred_on} — {CATEGORY_LABEL[e.category]}: {e.description}
                    {e.vendor ? ` (${e.vendor})` : ""}
                    {e.state !== "completed" && (
                      <> <em>{STATE_LABEL[e.state]}</em></>
                    )}
                    <button className="linkbtn tiny" disabled={expBusy}
                            onClick={() => startEditExpense(e)}>edit</button>
                    <button className="linkbtn tiny" disabled={expBusy}
                            onClick={() => setConfirmExpense(e)}>remove</button>
                  </span>
                  <span className="money">{formatMoney(e.amount_cents)}</span>
                </div>
              ))}
            </div>
          )}

          {cost.openLines > 0 && (
            <p className="taxnote">
              {cost.openLines} line{cost.openLines === 1 ? " is" : "s are"} still
              open, worth {formatMoney(cost.openCents)}. That money counts toward
              the cost already — once it is ordered it is owed — but the work is
              not finished, so this car is not fully costed yet.
            </p>
          )}

          {confirmExpense && (
            <Confirm
              message={
                `Remove ${CATEGORY_LABEL[confirmExpense.category]}: `
                + `${confirmExpense.description} for `
                + `${formatMoney(confirmExpense.amount_cents)}? `
                + (confirmExpense.state === "cancelled"
                    ? "It is already cancelled and not counting toward the car."
                    : "The car's total cost drops by that much.")
              }
              confirmLabel={expBusy ? "Removing" : "Remove expense"}
              onConfirm={() => removeExpense(confirmExpense.id)}
              onCancel={() => setConfirmExpense(null)}
            />
          )}
          {expErr && <ErrorNote>{expErr}</ErrorNote>}
          <SaveError save={expRemove} hint="The expense is still on the car." />
          {!showExpense && lastExpense.save.status === "saved" && (
            <p className="savenote">
              <SaveStatus save={lastExpense.save} savedLabel={lastExpense.label} />
            </p>
          )}

          {showExpense ? (
            <>
              <div className="grid2">
                <PastDateField label="Date" required value={ex.incurred_on}
                               onChange={(v2) => setEx((x) => ({ ...x, incurred_on: v2 }))} />
                <MoneyField label="Amount" value={ex.amount}
                            onChange={(v2) => setEx((x) => ({ ...x, amount: v2 }))} />
              </div>
              <TextField label="What for" required value={ex.description}
                     onChange={(v2) => setEx((x) => ({ ...x, description: v2 }))}
                     hint="Four tires, front bumper cover, full detail" />
              <div className="grid2">
                <Select label="Category" value={ex.category}
                        onChange={(v2) => setEx((x) => ({ ...x, category: v2 as ExpenseCategory }))}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
                <Select label="Status" value={ex.state}
                        onChange={(v2) => setEx((x) => ({ ...x, state: v2 as ExpenseState }))}>
                  {STATES.map((st) => (
                    <option key={st.value} value={st.value}>{st.label}</option>
                  ))}
                </Select>
              </div>
              <div className="grid2">
                <Select label="Vendor" value={ex.vendor}
                        onChange={(v2) => setEx((x) => ({ ...x, vendor: v2 }))}>
                  <option value="">—</option>
                  {VENDORS.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                  <option value="other">Other — type it</option>
                </Select>
                <TextField label="Invoice" value={ex.invoice}
                       onChange={(v2) => setEx((x) => ({ ...x, invoice: v2 }))} />
              </div>
              {ex.vendor === "other" && (
                <TextField label="Vendor name" value={ex.vendorOther}
                       onChange={(v2) => setEx((x) => ({ ...x, vendorOther: v2 }))} />
              )}
              <SaveBar save={expSave} onSave={() => void saveExpense()}
                       label={editingExpense ? "Save expense" : "Add expense"}
                       alwaysPrimary>
                <Button variant="ghost" disabled={expBusy}
                        onClick={() => {
                          if (expDirty && !window.confirm("Discard this expense?")) return;
                          setShowExpense(false); setEditingExpense(null); expSave.dismiss();
                        }}>
                  Cancel
                </Button>
              </SaveBar>
            </>
          ) : null}
          </Panel>

          <Panel title="Title and history" rail="record">
          <dl className="spec">
            <div>
              <dt>Title</dt>
              <dd className={v.title_status === "not_received" ? "warn" : ""}>
                {TITLE_LABEL[v.title_status] ?? v.title_status}
              </dd>
            </div>
            <div><dt>Title number</dt><dd className="mono">{v.title_number || "—"}</dd></div>
            <div><dt>Bought from</dt><dd>{v.purchased_from}</dd></div>
            <div><dt>Days in this status</dt><dd>{daysOn(v.status_changed_at)}</dd></div>
          </dl>
          </Panel>

          <h3 className="subhead">Move it along</h3>
          <div className="statusrow">
            {(["purchased", "in_recon", "available", "on_hold"] as const).map((s) => (
              <button key={s} className={`chip ${v.status === s ? "on" : ""}`}
                      disabled={busy || v.status === s}
                      onClick={() => changeStatus(s)}>
                {s === "in_recon" ? "In recon" : s === "on_hold" ? "On deposit"
                  : s === "available" ? "Available" : "Purchased"}
              </button>
            ))}
          </div>

          <p className="savenote"><SaveStatus save={actions} hideWhenIdle /></p>
          <SaveError save={actions} onRetry={undefined}
                     hint="Nothing was changed. Try again when the connection is back." />

          {confirming ? (
            <Confirm
              message={`Delete the ${v.year} ${v.make} ${v.model}, stock ${v.stock_no}? `
                     + "This cannot be undone."}
              confirmLabel={busy ? "Deleting" : "Delete vehicle"}
              onConfirm={remove}
              onCancel={() => setConfirming(false)}
            />
          ) : (
            <div className="actions">
              <Button variant="ghost"
                      onClick={() => { setF(formFrom(v)); editSave.reset(); setEditing(true); }}>
                Edit
              </Button>
              <Button variant="danger" onClick={() => setConfirming(true)}>Delete</Button>
            </div>
          )}
        </>
      )}

      {editing && (
        <>
          <div className="grid2">
            <Field label="Odometer" inputMode="numeric" value={f.odometer}
                   onChange={set("odometer")} error={errs.odometer} />
            <OptionSelect label="Color" value={f.color}
                          onChange={set("color")} options={COLORS} />
          </div>
          <TextField label="Trim" value={f.trim} onChange={set("trim")} />
          <div className="grid2">
            <Field label="Purchase price" inputMode="decimal" value={f.purchase}
                   onChange={set("purchase")} error={errs.purchase}
                   hint="What was paid for the car" />
            <Field label="Asking price" inputMode="decimal" value={f.ask}
                   onChange={set("ask")} error={errs.ask} />
          </div>
          <Field label="Minimum price" inputMode="decimal" value={f.minPrice}
                 onChange={set("minPrice")} error={errs.minPrice}
                 hint="What you would take. Never printed." />
          <Select label="Title" value={f.titleStatus} onChange={set("titleStatus")}>
            {TITLE_STATUSES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <TextField label="Title number" value={f.titleNumber} onChange={set("titleNumber")} />

          <SaveBar save={editSave} onSave={() => void saveEdits()}
                   label="Save changes" idle="No changes yet">
            <Button variant="ghost" onClick={cancelEdits} disabled={editSave.saving}>
              Cancel
            </Button>
          </SaveBar>
        </>
      )}
    </>
  );
}
